import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const SupportChat = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [userid, setUserid] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "technical",
    priority: "medium",
    message: "",
  });
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Load UserId from localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId) {
      setUserid(storedUserId);
    } else {
      toast.warning("No UserId found");
    }
  }, []);

  // Fetch tickets when userId changes
  useEffect(() => {
    if (userid) {
      fetchTickets();
    }
  }, [userid]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/ticketslist`, { userid });
      if (res.data.success) {
        setTickets(res.data.tickets);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  // Fetch single ticket details
  const loadTicketDetails = async (ticketId) => {
    try {
      const res = await axios.post(`${API_BASE}/ticketsdetails`, {
        ticket_id: ticketId,
      });
      if (res.data.success) {
        setSelectedTicket({
          ...res.data.ticket,
          messages: res.data.messages,
        });
      }
    } catch (err) {
      console.error("Error fetching ticket details:", err);
      toast.error("Failed to load ticket details");
    }
  };

  // Send message / reply
  const sendReply = async () => {
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API_BASE}/ticketsreply`, {
        ticket_id: selectedTicket.id,
        sender: "user",
        message: newMessage,
      });
      setNewMessage("");
      loadTicketDetails(selectedTicket.id);
      toast.success("Message sent successfully!");
    } catch (err) {
      console.error("Error sending reply:", err);
      toast.error("Failed to send message");
    }
  };

  // Create new ticket
  const createTicket = async () => {
    const { title, category, priority, message } = form;
    if (!title || !message) {
      toast.error("Please fill all fields!");
      return;
    }

    try {
      await axios.post(`${API_BASE}/ticketscreate`, {
        userid,
        title,
        category,
        priority,
        message,
      });
      setShowCreateModal(false);
      setForm({ title: "", category: "technical", priority: "medium", message: "" });
      fetchTickets();
      toast.success("Ticket created successfully!");
    } catch (err) {
      console.error("Error creating ticket:", err);
      toast.error("Failed to create ticket");
    }
  };

  // Close Ticket
  const closeTicket = async () => {
    if (!selectedTicket) return;
    if (!window.confirm("Are you sure you want to close this ticket?")) return;

    try {
      await axios.post(`${API_BASE}/ticketsupdate-status`, {
        ticket_id: selectedTicket.id,
        status: "closed",
      });
      toast.success("Ticket closed successfully");
      fetchTickets();
      loadTicketDetails(selectedTicket.id);
    } catch (err) {
      console.error("Error closing ticket:", err);
      toast.error("Failed to close ticket");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const statusIcons = {
    open: "🔴",
    "in-progress": "🟡",
    resolved: "🟢",
    closed: "⚫",
  };

  const priorityColors = {
    high: "priority-high",
    medium: "priority-medium",
    low: "priority-low",
  };

  const categoryIcons = {
    technical: "🔧",
    staking: "💰",
    referral: "👥",
    withdrawal: "🏦",
    deposit: "💳",
    general: "📄",
    other: "📝",
  };

  const filteredTickets = tickets.filter((t) => {
    if (activeTab === "all") return true;
    return t.status === activeTab;
  });

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="support-chat-page">
        <div className="page-header">
          <div>
            <h1>Support Tickets</h1>
            <p>Manage your support requests and get help from our team</p>
          </div>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <i className="fa fa-plus"></i> Create New Ticket
          </button>
        </div>

        <div className="ticket-container">
          {/* Ticket Sidebar */}
          <div className="ticket-sidebar">
            {/* Tabs */}
            <div className="filter-tabs">
              {["all", "open", "in-progress", "resolved", "closed"].map((tab) => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "all"
                    ? "All Tickets"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  <span className="tab-count">
                    {tab === "all"
                      ? tickets.length
                      : tickets.filter((t) => t.status === tab).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Ticket List */}
            <div className="ticket-list">
              {loading ? (
                <div className="loading-state">Loading tickets...</div>
              ) : filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`ticket-item ${
                      selectedTicket?.id === ticket.id ? "active" : ""
                    }`}
                    onClick={() => loadTicketDetails(ticket.id)}
                  >
                    <div className="ticket-header-row">
                      <div className="ticket-id">#{ticket.id}</div>
                      <div className={`priority-badge ${priorityColors[ticket.priority]}`}>
                        {ticket.priority}
                      </div>
                    </div>
                    <h3 className="ticket-title">{ticket.title}</h3>
                    <div className="ticket-meta">
                      <span className="ticket-category">
                        {categoryIcons[ticket.category]} {ticket.category}
                      </span>
                      <span className="ticket-date">
                        {new Date(ticket.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="ticket-footer">
                      <div className="ticket-status">
                        <span className="status-icon">{statusIcons[ticket.status]}</span>
                        <span className="status-text">{ticket.status}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <i className="fa fa-ticket"></i>
                  <p>No tickets found</p>
                </div>
              )}
            </div>
          </div>

          {/* Ticket Content */}
          <div className="ticket-content">
            {selectedTicket ? (
              <div className="ticket-details">
                <div className="details-header">
                  <div className="ticket-info">
                    <h2 className="ticket-detail-title">{selectedTicket.title}</h2>
                    <div className="ticket-detail-meta">
                      <div className="meta-item">
                        <span className="meta-label">Ticket ID:</span>
                        <span className="meta-value">#{selectedTicket.id}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Category:</span>
                        <span className="meta-value">
                          {categoryIcons[selectedTicket.category]} {selectedTicket.category}
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Created:</span>
                        <span className="meta-value">
                          {new Date(selectedTicket.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ticket-actions">
                    <div className={`status-badge status-${selectedTicket.status}`}>
                      {statusIcons[selectedTicket.status]} {selectedTicket.status}
                    </div>
                    {selectedTicket.status !== "closed" && (
                      <button className="close-ticket-btn" onClick={closeTicket}>
                        <i className="fa fa-times-circle"></i> Close Ticket
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages Section */}
                <div className="messages-section">
                  <h3 className="messages-title">Conversation</h3>
                  <div className="messages-list">
                    {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                      selectedTicket.messages.map((msg, index) => (
                        <div key={index} className={`message ${msg.sender}`}>
                          <div className="message-header">
                            <span className="message-sender">
                              {msg.sender === "user" ? "You" : "Support Team"}
                            </span>
                            <span className="message-time">
                              {new Date(msg.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="message-content">{msg.message}</div>
                        </div>
                      ))
                    ) : (
                      <div className="no-messages">
                        <i className="fa fa-comments"></i>
                        <p>No messages yet</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Section */}
                  {selectedTicket.status !== "closed" && (
                    <div className="reply-section">
                      <textarea
                        className="reply-input"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        rows="3"
                      />
                      <div className="reply-actions">
                        <button className="send-btn" onClick={sendReply}>
                          <i className="fa fa-paper-plane"></i> Send Message
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-ticket-selected">
                <div className="empty-state">
                  <i className="fa fa-ticket"></i>
                  <h3>No Ticket Selected</h3>
                  <p>Select a ticket from the list or create a new one.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Ticket Modal */}
        {showCreateModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Create New Ticket</h3>
                <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Enter ticket title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    <option value="technical">🔧 Technical</option>
                    <option value="deposit">💳 Deposit</option>
                    <option value="withdrawal">🏦 Withdrawal</option>
                    <option value="general">📄 General</option>
                    <option value="other">📝 Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <option value="high">🔴 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    placeholder="Describe your issue in detail..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows="5"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={createTicket}>
                  Submit Ticket
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SupportChat;